
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, HelpCircle, Check, ArrowRight, ArrowRightLeft } from 'lucide-react';
import { SynonymGroup, TargetVariant } from '../types';
import { LANGUAGES } from '../utils/languages';

interface SynonymGroupEditorProps {
    group?: SynonymGroup; // If editing existing group
    onSave: (group: SynonymGroup) => void;
    onCancel: () => void;
    isOpen: boolean;
    existingCategories?: string[]; // Suggestions for autocomplete
    defaultSourceLang?: string;
    defaultTargetLang?: string;
}

const SynonymGroupEditor: React.FC<SynonymGroupEditorProps> = ({
    group,
    onSave,
    onCancel,
    isOpen,
    existingCategories = [],
    defaultSourceLang,
    defaultTargetLang,
}) => {
    const [sources, setSources] = useState<string[]>(['']);
    const [targets, setTargets] = useState<TargetVariant[]>([
        { text: '', context: '', isDefault: true }
    ]);
    const [sourceLang, setSourceLang] = useState<string>('');
    const [targetLang, setTargetLang] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    // Load group data if editing
    useEffect(() => {
        if (group) {
            setSources(group.sources.length > 0 ? [...group.sources] : ['']);
            setTargets(group.targets.length > 0 ? [...group.targets] : [{ text: '', context: '', isDefault: true }]);
            setSourceLang(group.sourceLang || defaultSourceLang || '');
            setTargetLang(group.targetLang || defaultTargetLang || '');
        } else {
            setSources(['']);
            setTargets([{ text: '', context: '', isDefault: true }]);
            setSourceLang(defaultSourceLang || '');
            setTargetLang(defaultTargetLang || '');
        }
        setError(null);
    }, [group, isOpen, defaultSourceLang, defaultTargetLang]);

    // Handlers
    const handleAddSource = () => setSources([...sources, '']);
    const handleRemoveSource = (idx: number) => {
        if (sources.length === 1) {
            setSources(['']); // Don't remove last one, just clear it
            return;
        }
        setSources(sources.filter((_, i) => i !== idx));
    };
    const handleSourceChange = (idx: number, val: string) => {
        const newSources = [...sources];
        newSources[idx] = val;
        setSources(newSources);
    };

    const handleAddTarget = () => setTargets([...targets, { text: '', context: '', isDefault: false }]);
    const handleRemoveTarget = (idx: number) => {
        if (targets.length === 1) {
            setTargets([{ text: '', context: '', isDefault: true }]);
            return;
        }
        // If removing default, make the first one default
        const newTargets = targets.filter((_, i) => i !== idx);
        if (targets[idx].isDefault && newTargets.length > 0) {
            newTargets[0].isDefault = true;
        }
        setTargets(newTargets);
    };
    const handleTargetChange = (idx: number, field: keyof TargetVariant, val: any) => {
        const newTargets = [...targets];
        // If setting default, unset others
        if (field === 'isDefault' && val === true) {
            newTargets.forEach(t => t.isDefault = false);
        }
        (newTargets[idx] as any)[field] = val;
        setTargets(newTargets);
    };

    const handleSave = () => {
        const validSources = sources.map(s => s.trim()).filter(s => s);
        if (validSources.length === 0) return setError('Cần ít nhất một từ gốc');
        const validTargets = targets.filter(t => t.text.trim());
        if (validTargets.length === 0) return setError('Cần ít nhất một bản dịch');

        if (!validTargets.some(t => t.isDefault)) {
            validTargets[0].isDefault = true;
        }

        const newGroup: SynonymGroup = {
            id: group?.id || `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            name: validSources[0],
            category: group?.category || '',
            sources: validSources,
            targets: validTargets,
            sourceLang: sourceLang || undefined,
            targetLang: targetLang || undefined,
        };

        onSave(newGroup);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <h2 className="text-lg font-bold text-slate-800">
                        {group ? 'Sửa thuật ngữ' : 'Thêm thuật ngữ'}
                    </h2>
                    <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Lang pair selector */}
                    <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3 flex items-center gap-3">
                        <ArrowRightLeft className="w-4 h-4 text-violet-600 shrink-0" />
                        <span className="text-xs font-bold text-violet-700 uppercase tracking-wider whitespace-nowrap">Ngôn ngữ</span>
                        <select
                            value={sourceLang}
                            onChange={(e) => setSourceLang(e.target.value)}
                            className="flex-1 px-2.5 py-1.5 bg-white border border-violet-200 rounded-md text-sm text-slate-800 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none"
                        >
                            <option value="">— Chọn nguồn —</option>
                            {LANGUAGES.map(l => (
                                <option key={l.code} value={l.code}>{l.flag} {l.name} ({l.code.toUpperCase()})</option>
                            ))}
                        </select>
                        <ArrowRight className="w-4 h-4 text-violet-400 shrink-0" />
                        <select
                            value={targetLang}
                            onChange={(e) => setTargetLang(e.target.value)}
                            className="flex-1 px-2.5 py-1.5 bg-white border border-violet-200 rounded-md text-sm text-slate-800 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none"
                        >
                            <option value="">— Chọn đích —</option>
                            {LANGUAGES.map(l => (
                                <option key={l.code} value={l.code}>{l.flag} {l.name} ({l.code.toUpperCase()})</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Source Terms */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="flex items-center gap-1.5 text-xs font-bold text-indigo-600">
                                    <ArrowRight className="w-3.5 h-3.5" /> Từ gốc (Tiếng Việt)
                                </label>
                                <button onClick={handleAddSource} className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 hover:bg-indigo-50 px-2 py-1 rounded transition-colors">
                                    <Plus className="w-3 h-3" /> Thêm
                                </button>
                            </div>
                            <div className="space-y-2">
                                {sources.map((src, idx) => (
                                    <div key={idx} className="flex gap-2">
                                        <input
                                            type="text"
                                            value={src}
                                            onChange={e => handleSourceChange(idx, e.target.value)}
                                            placeholder="Nhập từ tiếng Việt..."
                                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-500 outline-none"
                                            autoFocus={idx === sources.length - 1 && sources.length > 1}
                                        />
                                        <button
                                            onClick={() => handleRemoveSource(idx)}
                                            disabled={sources.length === 1}
                                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Target Translations */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                                    <Check className="w-3.5 h-3.5" /> Nghĩa đích (Tiếng Anh)
                                </label>
                                <button onClick={handleAddTarget} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 hover:bg-emerald-50 px-2 py-1 rounded transition-colors">
                                    <Plus className="w-3 h-3" /> Thêm
                                </button>
                            </div>
                            <div className="space-y-2">
                                {targets.map((target, idx) => (
                                    <div key={idx} className="flex gap-2 items-center">
                                        <input
                                            type="text"
                                            value={target.text}
                                            onChange={e => handleTargetChange(idx, 'text', e.target.value)}
                                            placeholder="Bản dịch..."
                                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:border-emerald-500 outline-none"
                                        />
                                        <label className={`px-2 py-1.5 rounded-lg border cursor-pointer select-none transition-colors ${target.isDefault
                                            ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                                            : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                                            }`}>
                                            <input
                                                type="checkbox"
                                                checked={!!target.isDefault}
                                                onChange={() => handleTargetChange(idx, 'isDefault', true)}
                                                className="hidden"
                                            />
                                            <span className="text-[10px] font-bold uppercase tracking-wider">{target.isDefault ? 'Mặc định' : 'Đặt mặc định'}</span>
                                        </label>
                                        <button
                                            onClick={() => handleRemoveTarget(idx)}
                                            disabled={targets.length === 1}
                                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
                            <HelpCircle className="w-4 h-4" />
                            {error}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                    <button
                        onClick={onCancel}
                        className="px-5 py-2.5 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold transition-all flex items-center gap-2"
                    >
                        <Save className="w-4 h-4" /> Lưu
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SynonymGroupEditor;
